import { useState, useRef, useEffect } from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Highlighter,
  Image,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Paperclip,
  Redo,
  Strikethrough,
  Table2,
  Underline,
  Undo,
  Variable,
  X,
} from 'lucide-react';

/**
 * Rich Text Editor Component
 * User-friendly WYSIWYG editor with merge variables support
 */
export default function RichTextEditor({ 
  value = '', 
  onChange, 
  placeholder = 'Type your message...',
  variables = [],
  showVariables = true,
  showAttachments = false,
  attachments = [],
  onAttachmentsChange,
  minHeight = '200px',
  maxHeight = '520px',
}) {
  const editorRef = useRef(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showVariableMenu, setShowVariableMenu] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [fontFamily, setFontFamily] = useState('Default');
  const [fontSize, setFontSize] = useState('14px');
  const [textColor, setTextColor] = useState('#111827');
  const [highlightColor, setHighlightColor] = useState('#fef08a');
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Default merge variables for email campaigns
  const defaultVariables = [
    { key: 'first_name', label: 'First Name', example: 'John' },
    { key: 'last_name', label: 'Last Name', example: 'Smith' },
    { key: 'full_name', label: 'Full Name', example: 'John Smith' },
    { key: 'email', label: 'Email', example: 'john@example.com' },
    { key: 'organization', label: 'Organization', example: 'Acme Corp' },
    { key: 'phone', label: 'Phone', example: '(555) 123-4567' },
    { key: 'title', label: 'Job Title', example: 'Director' },
    { key: 'consultant_name', label: 'Consultant Name', example: 'Sarah Johnson' },
    { key: 'consultant_email', label: 'Consultant Email', example: 'sarah@stratel.com' },
    { key: 'consultant_phone', label: 'Consultant Phone', example: '(555) 987-6543' },
    { key: 'company_name', label: 'Company Name', example: 'Stratel Consulting' },
    { key: 'booking_link', label: 'Booking Link', example: 'https://stratel.com/book/sarah' },
    { key: 'unsubscribe_link', label: 'Unsubscribe Link', example: '#' },
  ];

  const allVariables = [...defaultVariables, ...variables];

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const applyFontFamily = (family) => {
    setFontFamily(family);
    const fontValue = family === 'Default' ? 'inherit' : family;
    execCommand('fontName', fontValue);
  };

  const applyFontSize = (size) => {
    setFontSize(size);
    const sizeMap = {
      '12px': '2',
      '14px': '3',
      '16px': '4',
      '18px': '5',
      '24px': '6',
      '32px': '7',
    };
    execCommand('fontSize', sizeMap[size] || '3');
  };

  const applyTextColor = (color) => {
    setTextColor(color);
    execCommand('foreColor', color);
  };

  const applyHighlight = (color) => {
    setHighlightColor(color);
    execCommand('hiliteColor', color);
  };

  const insertTable = () => {
    const tableHtml = `
      <table style="width: 100%; border-collapse: collapse;" border="1">
        <tbody>
          <tr><td>&nbsp;</td><td>&nbsp;</td></tr>
          <tr><td>&nbsp;</td><td>&nbsp;</td></tr>
        </tbody>
      </table>
      <p></p>
    `;
    execCommand('insertHTML', tableHtml);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      execCommand('insertImage', reader.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const insertLink = () => {
    if (linkUrl) {
      const text = linkText || linkUrl;
      const html = `<a href="${linkUrl}" style="color: #16a34a; text-decoration: underline;">${text}</a>`;
      document.execCommand('insertHTML', false, html);
      setShowLinkModal(false);
      setLinkUrl('');
      setLinkText('');
      handleInput();
    }
  };

  const insertVariable = (variable) => {
    const tag = `<span class="variable-tag" style="background-color: #dbeafe; color: #1e40af; padding: 2px 6px; border-radius: 4px; font-size: 13px;">{{${variable.key}}}</span>&nbsp;`;
    document.execCommand('insertHTML', false, tag);
    setShowVariableMenu(false);
    handleInput();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (onAttachmentsChange) {
      onAttachmentsChange([...attachments, ...files.map(f => ({ file: f, name: f.name }))]);
    }
  };

  const removeAttachment = (idx) => {
    if (onAttachmentsChange) {
      onAttachmentsChange(attachments.filter((_, i) => i !== idx));
    }
  };

  const ToolbarButton = ({ onClick, active, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-2 rounded hover:bg-gray-100 ${active ? 'bg-gray-100 text-green-600' : 'text-gray-600'}`}
    >
      {children}
    </button>
  );

  return (
    <div className="border rounded-lg overflow-hidden flex flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <select
            value={fontFamily}
            onChange={(e) => applyFontFamily(e.target.value)}
            className="border border-gray-200 rounded px-2 py-1 text-sm"
            title="Font family"
          >
            <option value="Default">Default</option>
            <option value="Arial">Arial</option>
            <option value="Georgia">Georgia</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Garamond">Garamond</option>
            <option value="Verdana">Verdana</option>
            <option value="Tahoma">Tahoma</option>
          </select>
          <select
            value={fontSize}
            onChange={(e) => applyFontSize(e.target.value)}
            className="border border-gray-200 rounded px-2 py-1 text-sm"
            title="Font size"
          >
            <option value="12px">12</option>
            <option value="14px">14</option>
            <option value="16px">16</option>
            <option value="18px">18</option>
            <option value="24px">24</option>
            <option value="32px">32</option>
          </select>
        </div>

        <div className="w-px h-6 bg-gray-300 mx-1" />
        <ToolbarButton onClick={() => execCommand('bold')} title="Bold">
          <Bold size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('italic')} title="Italic">
          <Italic size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('underline')} title="Underline">
          <Underline size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('strikeThrough')} title="Strikethrough">
          <Strikethrough size={18} />
        </ToolbarButton>
        
        <div className="w-px h-6 bg-gray-300 mx-1" />
        
        <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
          <List size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Numbered List">
          <ListOrdered size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('outdent')} title="Outdent">
          <AlignLeft size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('indent')} title="Indent">
          <AlignRight size={18} />
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Align Left">
          <AlignLeft size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Align Center">
          <AlignCenter size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyRight')} title="Align Right">
          <AlignRight size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyFull')} title="Justify">
          <AlignJustify size={18} />
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <ToolbarButton onClick={() => setShowLinkModal(true)} title="Insert Link">
          <LinkIcon size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => imageInputRef.current?.click()} title="Insert Image">
          <Image size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={insertTable} title="Insert Table">
          <Table2 size={18} />
        </ToolbarButton>

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
        />

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-gray-500" title="Text color">
            <span>Text</span>
            <input
              type="color"
              value={textColor}
              onChange={(e) => applyTextColor(e.target.value)}
              className="h-6 w-6 rounded border border-gray-200"
            />
          </label>
          <label className="flex items-center gap-1 text-xs text-gray-500" title="Highlight">
            <Highlighter size={14} />
            <input
              type="color"
              value={highlightColor}
              onChange={(e) => applyHighlight(e.target.value)}
              className="h-6 w-6 rounded border border-gray-200"
            />
          </label>
        </div>
        
        {showVariables && (
          <>
            <div className="w-px h-6 bg-gray-300 mx-1" />
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowVariableMenu(!showVariableMenu)}
                className="flex items-center gap-1 px-2 py-1.5 rounded hover:bg-gray-100 text-gray-600 text-sm"
                title="Insert Variable"
              >
                <Variable size={16} />
                <span>Variables</span>
              </button>
              
              {showVariableMenu && (
                <div className="absolute top-full left-0 mt-1 w-64 max-w-[calc(100vw-2rem)] bg-white border rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                  <div className="p-2 border-b bg-gray-50">
                    <span className="text-xs font-medium text-gray-500">Click to insert</span>
                  </div>
                  {allVariables.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => insertVariable(v)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex justify-between items-center"
                    >
                      <span className="text-sm font-medium">{v.label}</span>
                      <span className="text-xs text-gray-400 font-mono">{`{{${v.key}}}`}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        
        {showAttachments && (
          <>
            <div className="w-px h-6 bg-gray-300 mx-1" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 px-2 py-1.5 rounded hover:bg-gray-100 text-gray-600 text-sm"
              title="Attach File"
            >
              <Paperclip size={16} />
              <span>Attach</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </>
        )}
        
        <div className="flex-1" />
        
        <ToolbarButton onClick={() => execCommand('undo')} title="Undo">
          <Undo size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('redo')} title="Redo">
          <Redo size={18} />
        </ToolbarButton>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="p-4 pb-24 outline-none prose max-w-none overflow-y-auto"
        style={{ minHeight, maxHeight }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      {/* Attachments */}
      {showAttachments && attachments.length > 0 && (
        <div className="p-3 border-t bg-gray-50">
          <div className="text-xs font-medium text-gray-500 mb-2">Attachments</div>
          <div className="flex flex-wrap gap-2">
            {attachments.map((att, idx) => (
              <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-white border rounded text-sm">
                <Paperclip size={14} className="text-gray-400" />
                <span className="truncate max-w-[150px]">{att.name}</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(idx)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <h3 className="font-semibold text-gray-900 mb-4">Insert Link</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="https://example.com"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Text</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Click here (optional)"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={insertLink}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Insert Link
                </button>
                <button
                  type="button"
                  onClick={() => setShowLinkModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for placeholder */}
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] .variable-tag {
          background-color: #dbeafe;
          color: #1e40af;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 13px;
        }
      `}</style>
    </div>
  );
}
